import { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconForm } from '@components/icons/form';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Pagination, usePagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { storage } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { toQueryString } from '@utils/requests';

import { useDeleteTemplate, useFormTemplates } from 'api/forms/hooks';
import { ListFormTemplate } from 'api/forms/requests';
import { useWorkspace } from 'api/workspaces/hooks';
import { FavoriteMenuItem } from 'ui/authenticated/main/favorites/favorites';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { PageNotFound } from 'ui/authenticated/main/pages/page-not-found';
import { routePaths } from 'ui/authenticated/navigation';
import { useMoveFormTemplateModal } from 'ui/journey-builder/modals/move';

import { useDuplicateFormTemplateModal } from './duplicate';

const FORMS_PER_PAGE = 25;

const orderingItems = [
  'name',
  '-name',
  '-date_created',
  'date_created',
  '-date_updated',
  'date_updated',
] as const;

export const FormTemplatesList = () => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');
  const { workspaceId, title: workspaceTitle } = useCurrentWorkspace();
  const { isError: isWorkspaceError, isLoading: isWorkspaceLoading } = useWorkspace(workspaceId);
  const [ordering, setOrdering] = useLocalStorageState<(typeof orderingItems)[number]>({
    key: 'form-templates-list-order',
    defaultValue: '-date_created',
  });
  const queryString = useMemo(() => {
    return toQueryString({
      search: searchStr,
      workspace: workspaceId,
      ordering,
    });
  }, [searchStr, ordering, workspaceId]);

  const templatesQuery = useFormTemplates(queryString, isWorkspaceError ?? isWorkspaceLoading);

  const { numPages, selectedPage, selectPage, getItemsForPage } = usePagination({
    pageSize: FORMS_PER_PAGE,
    defaultPage: Number(storage.get('journey-forms-page')) || 1,
    count: (templatesQuery.data ?? []).length,
  });

  useEffect(() => {
    storage.set('journey-forms-page', String(selectedPage));
  }, [selectedPage]);

  useEffect(() => {
    if (selectedPage !== 1) {
      selectPage(1);
    }
  }, [templatesQuery.data]); // eslint-disable-line

  if (isWorkspaceError) {
    return <PageNotFound message={t('notFoundMessage')} />;
  }

  if (!templatesQuery.isSuccess) return null;

  const templates = getItemsForPage(templatesQuery.data) as ListFormTemplate[];
  const isDescendingOrdering = ordering.startsWith('-');

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: workspaceTitle }, { label: t('title') }]} />}
        actions={
          <Stack space={2} orientation="horizontal">
            <ExpandableSearch onChange={setSearchStr} />
            <Menu
              maxWidth="auto"
              width="auto"
              buttonProps={{
                variant: 'secondary',
                text: t(`sort_${ordering}`).split(' • ')[0],
                iconLeft: isDescendingOrdering ? <IconSortDesc /> : <IconSortAsc />,
              }}
            >
              <Menu.Label label={t('sort')} />
              {orderingItems.map((it) => (
                <Menu.Item
                  key={it}
                  onSelect={() => setOrdering(it)}
                  isActive={ordering === it}
                  label={t(`sort_${it}`)}
                />
              ))}
            </Menu>
            <Button
              variant="primary"
              iconLeft={<IconAddPlus />}
              href={generatePath(routePaths.workspaceFormTemplatesCreate, {
                workspaceId,
                '*': '',
              })}
              text={t('create')}
            />
          </Stack>
        }
      />
      <PageContent>
        {templates.length === 0 &&
          (searchStr ? (
            <EmptySearchState skipMargin />
          ) : (
            <EmptyState
              skipMargin
              header={t('emptyHeader')}
              content={t('emptyContent')}
              icon={<IconForm size={80} />}
            />
          ))}
        <Container>
          {templates.map((template) => (
            <TemplateRow key={template.shortId} template={template} />
          ))}
        </Container>
        <PaginationContainer>
          <Pagination
            justify="right"
            count={numPages}
            selected={selectedPage}
            onSelect={selectPage}
          />
        </PaginationContainer>
      </PageContent>
    </>
  );
};

const TemplateRow = ({ template }: { template: ListFormTemplate }) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const { workspaceId } = useCurrentWorkspace();
  const showDuplicateModal = useDuplicateFormTemplateModal();
  const showMoveModal = useMoveFormTemplateModal();
  const deleteMutation = useDeleteTemplate(template.shortId);
  return (
    <ListCard
      to={generatePath(routePaths.workspaceFormTemplatesEdit, {
        workspaceId,
        shortId: template.shortId,
        '*': '',
      })}
      title={template.name}
      description={template.description ?? ''}
      icon={<IconForm />}
      actions={
        <Menu
          buttonProps={{
            iconLeft: <IconMoreVertical />,
            variant: 'ghost',
          }}
          placement="left-start"
        >
          <Menu.Item
            label={t('duplicate')}
            icon={<IconDuplicate />}
            onSelect={() =>
              showDuplicateModal({
                template,
              })
            }
          />
          <FavoriteMenuItem contentType="formtemplate" shortId={template.shortId} />
          <Menu.Item
            label={t('move')}
            icon={<IconMove />}
            onSelect={() =>
              showMoveModal({
                formTemplate: template,
              })
            }
          />
          <Menu.Item
            variant="danger"
            label={t('delete')}
            icon={<IconTrash />}
            onSelect={() =>
              showDeleteModal({
                title: t('deleteForm'),
                description: t('areYouSureDelete', { name: template.name }),
                hasUserConfirmation: true,
                onConfirm: () => deleteMutation.mutate(),
              })
            }
          />
        </Menu>
      }
    />
  );
};

const locale = {
  en: {
    title: 'Forms',
    emptyHeader: 'No forms',
    emptyContent:
      'You haven’t created any forms yet, create your first form and start collecting details from your travellers',
    create: 'Create form',
    delete: 'Delete',
    deleteForm: 'Delete form',
    areYouSureDelete: 'Are you sure you want to delete the form: {name}?',
    duplicate: 'Duplicate',
    move: 'Move to workspace',
    sort: 'Sort by',
    sort_name: 'Alphabetical ascending • A → Z',
    'sort_-name': 'Alphabetical descending • Z → A',
    'sort_-date_created': 'Created • Newest',
    sort_date_created: 'Created • Oldest',
    'sort_-date_updated': 'Last edit • New → Old',
    sort_date_updated: 'Last edit • Old → New',
    notFoundMessage: 'Are you sure the workspace exists and you have access to it?',
  },
};

const Container = styled(Stack).attrs({ space: 2 })({});

const PaginationContainer = styled.div({
  paddingTop: pxToRem(24),
  paddingBottom: pxToRem(24),
});
